/*
 * The viewer of a 360° photo (1.173.0, #154) -- one file for the app and
 * for the EPUB.
 *
 * From use: "Hochladen und Anzeigen von 360-Grad Fotos", in the web app and
 * in the EPUB, decided "Versuch: Betrachter im EPUB". Two copies of the same
 * drawing would drift apart; so the app imports this file
 * (`features/thread/PanoView.tsx`), and `EpubBook` puts it into the book as
 * a script, with `export` taken off (`EpubBook::panoScript()`). It therefore
 * stays plain JavaScript without imports and without words: whoever shows it
 * brings the words.
 *
 * Drawn with WebGL in one fragment shader: each point of the screen is a
 * direction, the direction is a longitude and a latitude, and those are a
 * point of the equirectangular picture. No library -- three.js would do the
 * same with a sphere of triangles and is not in the book.
 *
 * Without WebGL `mountPano()` returns null, and the caller keeps the flat
 * picture. Nothing goes anywhere: the picture comes from where the page
 * comes from, and the motion of the device stays in the device.
 */

const VERTEX = 'attribute vec2 p; varying vec2 v; void main() { v = p; gl_Position = vec4(p, 0.0, 1.0); }'

// The camera looks along -z; `pitch` up is positive, `yaw` to the right.
const FRAGMENT = [
  '#ifdef GL_FRAGMENT_PRECISION_HIGH',
  'precision highp float;',
  '#else',
  'precision mediump float;',
  '#endif',
  'varying vec2 v;',
  'uniform sampler2D picture;',
  'uniform float yaw;',
  'uniform float pitch;',
  'uniform float lens;',
  'uniform float aspect;',
  'const float PI = 3.14159265358979;',
  'void main() {',
  '  vec3 d = normalize(vec3(v.x * lens * aspect, v.y * lens, -1.0));',
  '  float cp = cos(pitch); float sp = sin(pitch);',
  '  d = vec3(d.x, d.y * cp - d.z * sp, d.y * sp + d.z * cp);',
  '  float cy = cos(yaw); float sy = sin(yaw);',
  '  d = vec3(d.x * cy - d.z * sy, d.y, d.x * sy + d.z * cy);',
  '  float lon = atan(d.x, -d.z);',
  '  float lat = asin(clamp(d.y, -1.0, 1.0));',
  '  gl_FragColor = texture2D(picture, vec2(lon / (2.0 * PI) + 0.5, 0.5 - lat / PI));',
  '}',
].join('\n')

/** The narrowest and widest view, vertical, in degrees. */
const FOV_MIN = 30
const FOV_MAX = 100
const FOV_START = 75

/** Whether this browser can draw a sphere at all. */
export function panoramaSupported() {
  try {
    const canvas = document.createElement('canvas')
    return Boolean(canvas.getContext('webgl'))
  } catch (_) {
    return false
  }
}

/** A 3×3 matrix product, row-major. */
function times(a, b) {
  const out = []
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      out.push(a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c])
    }
  }
  return out
}
function aboutX(t) { const c = Math.cos(t), s = Math.sin(t); return [1, 0, 0, 0, c, -s, 0, s, c] }
function aboutY(t) { const c = Math.cos(t), s = Math.sin(t); return [c, 0, s, 0, 1, 0, -s, 0, c] }
function aboutZ(t) { const c = Math.cos(t), s = Math.sin(t); return [c, -s, 0, s, c, 0, 0, 0, 1] }

/**
 * Where the back of the device looks, as yaw and pitch in radians.
 *
 * The rotation of `deviceorientation` is Z-X'-Y'' (alpha, beta, gamma); the
 * camera of a phone held upright looks along its -z, which is the device's
 * -y turned by -90° about x; and the screen may be turned. The same chain as
 * three.js's DeviceOrientationControls, as matrices. Exported for the probe,
 * which checks the upright, the flat and the turned phone.
 */
export function deviceView(alpha, beta, gamma, screenAngle) {
  const rad = Math.PI / 180
  let m = times(aboutY(alpha * rad), aboutX(beta * rad))
  m = times(m, aboutZ(-gamma * rad))
  m = times(m, aboutX(-Math.PI / 2))
  m = times(m, aboutZ(-screenAngle * rad))
  // The forward direction (0, 0, -1) is the negated third column.
  const fx = -m[2], fy = -m[5], fz = -m[8]
  return { yaw: Math.atan2(fx, -fz), pitch: Math.asin(Math.max(-1, Math.min(1, fy))) }
}

/**
 * Shows the 360° picture at `source` in `host` and returns a handle, or null
 * without WebGL. The host is filled; the caller sizes it.
 *
 * `options.onState(state)` hears `'ready'` once the picture is drawn and
 * `'failed'` when it could not be loaded or drawn.
 */
export function mountPano(host, source, options) {
  const onState = (options && options.onState) || function () {}
  const canvas = document.createElement('canvas')
  canvas.className = 'panoview__canvas'
  canvas.style.width = '100%'
  canvas.style.height = '100%'
  canvas.style.display = 'block'
  canvas.style.touchAction = 'none'
  const gl = canvas.getContext('webgl', { antialias: false, preserveDrawingBuffer: true })
  if (!gl) return null
  host.appendChild(canvas)

  const compile = (type, text) => {
    const shader = gl.createShader(type)
    gl.shaderSource(shader, text)
    gl.compileShader(shader)
    return shader
  }
  const program = gl.createProgram()
  gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX))
  gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT))
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    canvas.remove()
    return null
  }
  gl.useProgram(program)
  // One triangle over the whole screen.
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer())
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
  const position = gl.getAttribLocation(program, 'p')
  gl.enableVertexAttribArray(position)
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
  const at = (name) => gl.getUniformLocation(program, name)
  const uniforms = { yaw: at('yaw'), pitch: at('pitch'), lens: at('lens'), aspect: at('aspect') }

  const view = { yaw: (options && options.yaw) || 0, pitch: 0, fov: FOV_START }
  // How far a drag has turned the view away from where the device points.
  let motionOffset = 0
  let loaded = false
  let frame = 0
  let destroyed = false

  const draw = () => {
    frame = 0
    if (!loaded || destroyed) return
    const ratio = Math.min(window.devicePixelRatio || 1, 2)
    const width = Math.max(1, Math.round(canvas.clientWidth * ratio))
    const height = Math.max(1, Math.round(canvas.clientHeight * ratio))
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width
      canvas.height = height
    }
    gl.viewport(0, 0, width, height)
    gl.uniform1f(uniforms.yaw, view.yaw)
    gl.uniform1f(uniforms.pitch, view.pitch)
    gl.uniform1f(uniforms.lens, Math.tan((view.fov * Math.PI) / 360))
    gl.uniform1f(uniforms.aspect, width / height)
    gl.drawArrays(gl.TRIANGLES, 0, 3)
  }
  const redraw = () => {
    if (frame === 0 && !destroyed) frame = requestAnimationFrame(draw)
  }

  // The picture. Larger than the device's largest texture: drawn smaller
  // first (an older iPhone takes 4096, a sphere of 8192 would stay black).
  const picture = new Image()
  picture.decoding = 'async'
  picture.onload = () => {
    if (destroyed) return
    const largest = gl.getParameter(gl.MAX_TEXTURE_SIZE)
    let pixels = picture
    if (picture.naturalWidth > largest) {
      const smaller = document.createElement('canvas')
      smaller.width = largest
      smaller.height = Math.round((largest * picture.naturalHeight) / picture.naturalWidth)
      smaller.getContext('2d').drawImage(picture, 0, 0, smaller.width, smaller.height)
      pixels = smaller
    }
    gl.bindTexture(gl.TEXTURE_2D, gl.createTexture())
    // Clamped and without mipmaps: works for any size in WebGL 1, and at the
    // seam behind the viewer the last column meets the first.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, pixels)
    } catch (_) {
      onState('failed')
      return
    }
    loaded = true
    draw()
    host.setAttribute('data-pano-ready', '')
    onState('ready')
  }
  picture.onerror = () => onState('failed')
  picture.src = source

  // **Looking around**: one finger or the mouse turns the view, two fingers
  // or the wheel change how much is seen. The events stop here, so the page
  // below (the full view, the book) does not swipe or close.
  const fingers = new Map()
  let pinch = null
  const turn = (dx, dy) => {
    const perPoint = ((view.fov * Math.PI) / 180) / Math.max(1, canvas.clientHeight)
    view.yaw -= dx * perPoint
    view.pitch = Math.max(-1.55, Math.min(1.55, view.pitch + dy * perPoint))
    motionOffset -= dx * perPoint
    redraw()
  }
  const zoomTo = (fov) => {
    view.fov = Math.max(FOV_MIN, Math.min(FOV_MAX, fov))
    redraw()
  }
  const stop = (event) => event.stopPropagation()
  canvas.addEventListener('pointerdown', (event) => {
    stop(event)
    canvas.setPointerCapture(event.pointerId)
    fingers.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (fingers.size === 2) {
      const [a, b] = [...fingers.values()]
      pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y), fov: view.fov }
    }
  })
  canvas.addEventListener('pointermove', (event) => {
    stop(event)
    const last = fingers.get(event.pointerId)
    if (!last) return
    const now = { x: event.clientX, y: event.clientY }
    fingers.set(event.pointerId, now)
    if (fingers.size === 2 && pinch) {
      const [a, b] = [...fingers.values()]
      zoomTo((pinch.fov * pinch.distance) / Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)))
    } else if (fingers.size === 1) {
      turn(now.x - last.x, now.y - last.y)
    }
  })
  const lift = (event) => {
    stop(event)
    fingers.delete(event.pointerId)
    if (fingers.size < 2) pinch = null
  }
  canvas.addEventListener('pointerup', lift)
  canvas.addEventListener('pointercancel', lift)
  // Touches too: in the book nothing else stops them, and in the app the full
  // view's own exemption (`.lightbox__pano`) is a second lock, not this one.
  for (const name of ['touchstart', 'touchmove', 'touchend', 'click', 'dblclick']) {
    canvas.addEventListener(name, stop)
  }
  canvas.addEventListener('wheel', (event) => {
    stop(event)
    event.preventDefault()
    zoomTo(view.fov * (1 + event.deltaY / 500))
  }, { passive: false })

  // **Turning with the device**, on request only: iOS asks for permission,
  // and only inside a tap (`setMotion()` is called from one). Dragging while
  // it is on moves the offset, so the view does not jump back.
  let motionStart = null
  const onOrientation = (event) => {
    if (event.alpha === null || event.beta === null || event.gamma === null) return
    const angle = (screen.orientation && screen.orientation.angle) || window.orientation || 0
    const seen = deviceView(event.alpha, event.beta, event.gamma, angle)
    if (motionStart === null) {
      motionStart = seen
      motionOffset = view.yaw - seen.yaw
    }
    view.yaw = seen.yaw + motionOffset
    view.pitch = Math.max(-1.55, Math.min(1.55, seen.pitch))
    redraw()
  }
  let motion = false
  const setMotion = async (on) => {
    if (on === motion) return motion
    if (!on) {
      window.removeEventListener('deviceorientation', onOrientation)
      motion = false
      return false
    }
    const ask = typeof DeviceOrientationEvent !== 'undefined' && DeviceOrientationEvent.requestPermission
    if (ask) {
      try {
        if ((await DeviceOrientationEvent.requestPermission()) !== 'granted') return false
      } catch (_) {
        return false
      }
    }
    motionStart = null
    window.addEventListener('deviceorientation', onOrientation)
    motion = true
    return true
  }

  const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(redraw)
  if (resized) resized.observe(canvas)

  return {
    setMotion,
    /** Whether this device reports its orientation at all. */
    motionAvailable: () => typeof DeviceOrientationEvent !== 'undefined',
    view: () => ({ yaw: view.yaw, pitch: view.pitch, fov: view.fov }),
    destroy: () => {
      destroyed = true
      if (frame !== 0) cancelAnimationFrame(frame)
      window.removeEventListener('deviceorientation', onOrientation)
      if (resized) resized.disconnect()
      const lose = gl.getExtension('WEBGL_lose_context')
      if (lose) lose.loseContext()
      canvas.remove()
    },
  }
}

/**
 * The 360° photos of a page of the book (`EpubBook`): each `figure.pano`
 * gets a button that opens the viewer in place of the flat picture, and the
 * flat picture stays where WebGL or scripts are missing. The words come from
 * the book (`data-open`, `data-close`, `data-motion`).
 */
export function mountBookPanos(root) {
  if (!panoramaSupported()) return
  const figures = root.querySelectorAll('figure.pano')
  for (let i = 0; i < figures.length; i++) {
    const figure = figures[i]
    const flat = figure.querySelector('img')
    if (!flat) continue
    const open = root.createElement('button')
    open.type = 'button'
    open.className = 'pano__open'
    open.textContent = figure.getAttribute('data-open') || '360°'
    figure.appendChild(open)
    let handle = null
    let stage = null
    open.addEventListener('click', () => {
      if (handle) return
      stage = root.createElement('div')
      stage.className = 'pano__stage'
      figure.insertBefore(stage, flat)
      handle = mountPano(stage, flat.getAttribute('src'), {})
      if (!handle) {
        stage.remove()
        return
      }
      flat.style.display = 'none'
      open.style.display = 'none'
      const bar = root.createElement('div')
      bar.className = 'pano__bar'
      const motion = root.createElement('button')
      motion.type = 'button'
      motion.textContent = figure.getAttribute('data-motion') || '↻'
      motion.addEventListener('click', () => {
        handle.setMotion(motion.getAttribute('aria-pressed') !== 'true').then((on) => {
          motion.setAttribute('aria-pressed', on ? 'true' : 'false')
        })
      })
      const close = root.createElement('button')
      close.type = 'button'
      close.textContent = figure.getAttribute('data-close') || '×'
      close.addEventListener('click', () => {
        handle.destroy()
        handle = null
        stage.remove()
        bar.remove()
        flat.style.display = ''
        open.style.display = ''
      })
      if (handle.motionAvailable()) bar.appendChild(motion)
      bar.appendChild(close)
      figure.appendChild(bar)
    })
  }
}
